import type { HomeHeroStateKind } from "./home-hero-state";

/**
 * Plan 435 M2 (mfh-hero-analytics). Allowlist-only event names + typed
 * capture helpers for Home's hero (tech-spec Schema Changes). Mirrors the
 * `lib/marketplace-analytics.ts` idiom — no ids, names, or dollar amounts
 * ever cross into a property.
 */

export type HomeCapture = (
  event: string,
  properties?: Record<string, unknown>,
) => void;

export type HomeHeroCtaKind = "primary" | "secondary" | "chip" | "best_offer";
export const HOME_ANALYTICS_EVENTS = {
  homeViewed: "home_viewed",
  heroCtaClicked: "home_hero_cta_clicked",
  agentPreviewClicked: "home_agent_preview_clicked",
  offerTeaserClicked: "home_offer_teaser_clicked",
  tierUnlockClicked: "home_tier_unlock_clicked",
} as const;

export function captureHomeViewed(
  capture: HomeCapture,
  heroState: HomeHeroStateKind,
  agentPreviewCount: number,
  offersCount: number,
) {
  capture(HOME_ANALYTICS_EVENTS.homeViewed, {
    hero_state: heroState,
    agent_preview_count: agentPreviewCount,
    offers_count: offersCount,
  });
}

export function captureHomeHeroCtaClicked(
  capture: HomeCapture,
  heroState: HomeHeroStateKind,
  cta: HomeHeroCtaKind,
) {
  capture(HOME_ANALYTICS_EVENTS.heroCtaClicked, {
    hero_state: heroState,
    cta,
  });
}

/** Plan 435 M3 (mfh-block-analytics). `position` is 1-indexed — the
 *  buyer-facing rank on the tile, never an array index. */
export function captureHomeAgentPreviewClicked(
  capture: HomeCapture,
  position: number,
  heroState: HomeHeroStateKind,
) {
  capture(HOME_ANALYTICS_EVENTS.agentPreviewClicked, {
    position,
    hero_state: heroState,
  });
}

/** Home renders only the made-for-you offer card (standing offers show as
 *  agent tiles), so the event's `kind` is always `active`. */
export function captureHomeOfferTeaserClicked(capture: HomeCapture) {
  capture(HOME_ANALYTICS_EVENTS.offerTeaserClicked, { kind: "active" });
}

/** `currentTier`/`lockedTier` are offer-tier strings ("t1"/"t2") — never a
 *  pro or offer id. */
export function captureHomeTierUnlockClicked(
  capture: HomeCapture,
  currentTier: string,
  lockedTier: string,
) {
  capture(HOME_ANALYTICS_EVENTS.tierUnlockClicked, {
    current_tier: currentTier,
    locked_tier: lockedTier,
  });
}
